import { Spacing, Text, Title } from '@vkontakte/vkui';
import imgModal from 'img/quota-landing/upsalemodal.png';
import React, { memo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { tariffUpsale } from 'reactApp/appHelpers/featuresHelpers/features/upsaleSmallTariffs';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './UpsaleModal.css';

type UpsaleModalProps = {
    onClose?: () => void;
    selectedProduct: Product;
    activePeriod: 'm' | 'y';
    resolve: (product: Product) => void;
};

const sendAnalytics = (action) => {
    sendPaymentGa({ eventCategory: 'splashscreen-alert', action, name: 'mnz_upsale_unlimited_autoload' });
};

export const UpsaleModal = memo<UpsaleModalProps>(({ onClose, selectedProduct, activePeriod, resolve }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const monthProductUpTariff = useSelector((state: RootState) => ProductsSelectors.getProductById(state, tariffUpsale?.month));
    const yearProductUpTariff = useSelector((state: RootState) => ProductsSelectors.getProductById(state, tariffUpsale?.year));
    const valueSmallTariff = selectedProduct.space.value;
    const monthProduct = activePeriod === 'm' ? selectedProduct : undefined;
    const yearProduct = activePeriod === 'y' ? selectedProduct : undefined;
    sendAnalytics('show');
    emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_SHOW);

    const handleClose = useCallback(() => {
        sendAnalytics('close');
        emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_CLOSE);
        onClose?.();
    }, [onClose]);

    const handleClickByTariffUpsale = useCallback(() => {
        sendAnalytics('click');
        emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_CLICK);
        onClose?.();
        if (activePeriod === 'm' && monthProductUpTariff) {
            resolve(monthProductUpTariff);
        } else if (activePeriod === 'y' && yearProductUpTariff) {
            resolve(yearProductUpTariff);
        }
    }, [activePeriod, monthProduct, yearProduct, resolve]);

    const handleClickBySmallTariff = useCallback(() => {
        sendAnalytics('click-reject');
        emitAnalyticEvent(AnalyticEventNames.UPSALE_BAZ_MODAL_REJECT);
        onClose?.();
        if (monthProduct) {
            resolve(monthProduct);
        } else if (yearProduct) {
            resolve(yearProduct);
        }
    }, [activePeriod, monthProduct, yearProduct, resolve]);

    return (
        <MobileDialog
            id="mobile-upsale-modal"
            onClose={handleClose}
            closeOnDimmerClick
            topmost
            mod="base"
            contextPadding={'zero'}
            scrollRef={scrollRef}
        >
            <div ref={scrollRef} className={styles.root}>
                <div className={styles.imgWrapper}>
                    <img src={imgModal} alt="Промо безлимита" className={styles.image} />
                </div>
                <div className={styles.textBlock}>
                    <Title level="1">C&nbsp;тарифом {monthProductUpTariff?.space.value} у&nbsp;вас будет безлимит</Title>
                    <Spacing size={12} />
                    <Text className={styles.text}>
                        Цена чуть выше, зато с&nbsp;безлимитом <br />
                        фото с&nbsp;телефона не&nbsp;тратят память Облака. Попробуйте в&nbsp;деле
                    </Text>
                </div>
                <div className={styles.buttonBlock}>
                    <Button theme="vk" sizeMode={ButtonSizeMode.big} onClick={handleClickByTariffUpsale} primary>
                        Беру {monthProductUpTariff?.space.value} с&nbsp;безлимитом
                    </Button>
                    <Spacing size={8} />
                    <Button theme="vk" sizeMode={ButtonSizeMode.big} onClick={handleClickBySmallTariff}>
                        Беру только {valueSmallTariff}
                    </Button>
                </div>
            </div>
        </MobileDialog>
    );
});

UpsaleModal.displayName = 'UpsaleModal';
